import React, {PureComponent} from "react";
import {connect, DispatchProp} from "react-redux";
import {IApplicationState} from "../../store";
import {SceneryType} from "../../store/settings/SceneryType";
import {cssHelper} from "../../shared/utils/cssHelper";
import {range} from "../../shared/utils/range";
import {leaveChestGame, selectChest} from "../../store/mini-games/chests/actions";
import styles from "./chest-mini-game.module.css";
import minigameStyles from "../../shared/styles/minigame.module.css";
import {IAnimationSettings} from "../../store/settings/types";
import {Animation, Back, Power4, Power2, TimelineLite, TweenLite, TweenMax} from "gsap";
import {BonusType} from "../../store/player/types";
import playBackground from "../../shared/sounds/playBackground";
import {shuffle} from "../../shared/utils/shuffle";
import {Random} from "../../shared/utils/random";
import renderText from "../../shared/components/renderText";
import playSound from "../../shared/sounds/playSound";
import getFontSize from "../../shared/utils/getFontSize";
import i18n from "../../shared/i18n";

interface ChestMiniGameSceneProps {
    levelScenery: SceneryType;
    isFirstSelect: boolean;
    isRevealed: boolean;
    isWon: boolean;
    chestIndex: number;
    chestCount: number;
    chestsWinRate: number;
    randomSeed: number;
    animation: IAnimationSettings;
}

class ChestMiniGameScene extends PureComponent<ChestMiniGameSceneProps & DispatchProp> {
    private readonly random: Random;
    private readonly header: React.RefObject<HTMLDivElement>;
    private readonly footer: React.RefObject<HTMLDivElement>;
    private readonly chests: React.RefObject<HTMLDivElement>;
    private readonly activeTweens: Animation[] = [];

    constructor(props: ChestMiniGameSceneProps & DispatchProp, context: any) {
        super(props, context);

        this.header = React.createRef();
        this.footer = React.createRef();
        this.chests = React.createRef();

        this.random = new Random(this.props.randomSeed);

        this.state = {
            enabled: false
        };
    }

    render() {
        const {levelScenery, isRevealed} = this.props;
        const chests = this.renderChests();

        return (
            <section className={cssHelper(minigameStyles.container).add(`${levelScenery}_chest_bg`).toString()} onClick={isRevealed ? () => this.close() : undefined}>
                <header className={minigameStyles.caption} ref={this.header}>
                    {renderText(i18n("gameplay.chestsminigame.pickchest"), 55, 70, 13, 22)}
                </header>
                <div className={styles.chests} ref={this.chests}>
                    {chests}
                </div>
                <footer className={cssHelper(minigameStyles.footer).add(minigameStyles.footer_revealed, this.props.isRevealed).toString()} ref={this.footer}>
                    {renderText(i18n("gameplay.chestsminigame.taptocontinue"), 36, 55, 15, 30)}
                </footer>
            </section>
        );
    }

    componentDidMount(): void {
        playBackground('bonus-game');

        const {animation} = this.props;

        if (!this.chests.current || !animation.enabled)
            return;

        const chests = this.chests.current.querySelectorAll(`.${styles.chest}`);
        const shadows = this.chests.current.querySelectorAll(`.${styles.chest_shadow}`);

        for (let i = 0; i < chests.length; i++) {
            let tween = TweenLite.from(chests[i], 0.3,{
                y: -200,
                autoAlpha: 0,
                delay: 0.15 * i,
                ease: Power4.easeIn
            }).timeScale(animation.timeScale);

            this.activeTweens.push(tween);

            tween = TweenLite.from(shadows[i], 0.3,{
                scale: 0.5,
                autoAlpha: 0,
                delay: 0.15 * i
            }).timeScale(animation.timeScale);

            this.activeTweens.push(tween);

            tween = TweenMax.to(chests[i], 0.05,{
                scaleY: 0.9,
                repeat: 1,
                yoyo: true,
                delay: 0.3 + 0.15 * i,
                onStart: () => playSound("chest-drop"),
                ease: Back.easeOut.config(3)
            }).timeScale(animation.timeScale);

            this.activeTweens.push(tween);
        }

        if (this.header.current) {
            const tween = TweenLite.from(this.header.current, 0.33, {
                autoAlpha: 0,
                scale: 0.7,
                delay: 1,
                ease: Back.easeOut.config(5)
            }).timeScale(animation.timeScale);

            this.activeTweens.push(tween);
        }
    }

    componentDidUpdate(prevProps: Readonly<ChestMiniGameSceneProps & DispatchProp>, prevState: Readonly<{}>, snapshot?: any): void {
        const {chestIndex, animation} = this.props;

        if (this.props.isRevealed && !prevProps.isRevealed) {
            if (this.chests.current) {
                const chests = this.chests.current.querySelectorAll(`.${styles.chest}`);
                const openChests = this.chests.current.querySelectorAll(`.${styles.chest_open}`);

                for (let i = 0; i < chests.length; i++) {
                    if (animation.enabled) {
                        const timeLine = TweenMax
                            .to(openChests[i], 0.2, {
                                scale: 1.1,
                                y: -20,
                                repeat: 1,
                                yoyo: true,
                                ease: Power2.easeInOut,
                                delay: i === chestIndex ? 0 : (i + 5) * 0.1,
                                onStart: () => {
                                    TweenLite.set(chests[i], {
                                        autoAlpha: 0,
                                    });

                                    TweenLite.set(openChests[i], {
                                        autoAlpha: 1,
                                    });

                                    playSound("chest-open", i === chestIndex ? 1.0 : 0.5);
                                }
                            })
                            .timeScale(animation.timeScale);

                        this.activeTweens.push(timeLine);
                    } else {
                        TweenLite.set(chests[i], {
                            autoAlpha: 0,
                        });

                        TweenLite.set(openChests[i], {
                            autoAlpha: 1,
                        });

                        playSound("chest-open");
                    }
                }
            }

            if (this.header.current) {
                if (animation.enabled) {
                    const timeLine = new TimelineLite({ delay: 1 });

                    timeLine.to(this.header.current, 0.33, {
                        autoAlpha: 0,
                        scale: 0.7
                    });

                    timeLine.call(() => {
                        this.updateHeaderText();
                    });

                    timeLine.to(this.header.current, 0.33, {
                        autoAlpha: 1,
                        scale: 1,
                        ease: Back.easeOut.config(5)
                    });

                    timeLine.timeScale(animation.timeScale);

                    this.activeTweens.push(timeLine);
                } else {
                    this.updateHeaderText();
                }
            }

            if (this.footer.current) {
                if (animation.enabled) {
                    const tween = TweenMax.to(this.footer.current, 1.33, {
                        autoAlpha: 1,
                        delay: 1,
                        repeat: -1,
                        yoyo: true,
                        ease: Power4.easeInOut
                    }).timeScale(animation.timeScale);

                    this.activeTweens.push(tween);
                } else {
                    TweenLite.set(this.footer.current, {
                        autoAlpha: 1,
                    });
                }
            }
        }
    }

    componentWillUnmount(): void {
        for (let animation of this.activeTweens) {
            animation.kill();
        }
    }

    private renderChests() {
        const {isWon, isRevealed, chestCount, chestIndex, chestsWinRate, levelScenery} = this.props;

        const angle = 360 / chestCount;
        const wonCount = Math.floor(chestCount * chestsWinRate);
        const chestEntries = shuffle(range(wonCount).map(() => true)
            .concat(range(chestCount - wonCount).map(() => false)));

        const initialIndex = chestEntries.indexOf(isWon) - chestIndex;

        return range(chestCount).map((i) => {
            const className = cssHelper(styles.chest_open);

            let index = (initialIndex + i) % chestCount;
            if (index < 0) {
                index = chestCount + index;
            }

            if (chestEntries[index]) {
                className.add(`${levelScenery}_chest_open_prize`);
            } else {
                className.add(`${levelScenery}_chest_open_empty`);
            }

            return (
                <div key={i} className={styles.chest_wrapper} style={{ transform: `rotate(${i * angle}deg) translateX(225px) rotate(-${i * angle}deg)` }}>
                    <div className={styles.chest_shadow}/>
                    <div className={className.toString()}/>
                    <div className={cssHelper(styles.chest, `${levelScenery}_chest_closed`).toString()} onClick={() => !isRevealed ? this.select(i): undefined}/>
                </div>
            );
        });
    }

    private select(chestIndex: number) {
        const isWon = this.props.isFirstSelect || this.random.next() < this.props.chestsWinRate;

        this.props.dispatch(selectChest({
            chestIndex,
            isWon
        }));
    }

    private close() {
        this.props.dispatch(leaveChestGame({
            isAnswered: this.props.isRevealed,
            isRight: this.props.isWon,
            bonus: this.props.isWon ? BonusType.Coins : undefined
        }));
    }

    private updateHeaderText() {
        const {isWon} = this.props;

        if (this.header.current) {
            let text;

            if (isWon) {
                text = i18n("gameplay.chestsminigame.wontext");
                playSound("bonus-win");
            } else {
                text = i18n("gameplay.chestsminigame.losttext");
                playSound("bonus-fail");
            }

            this.header.current.innerHTML = text;
            this.header.current.style.fontSize = getFontSize(text.length, 45, 70, 12, 30) + "px";
        }
    }
}

export default connect(({ game, miniGames: { chests }, settings }: IApplicationState): ChestMiniGameSceneProps => ({
    ...chests,
    levelScenery: game.levelScenery,
    chestsWinRate: settings.miniGames.chestsWinRate,
    chestCount: settings.miniGames.chestCount,
    randomSeed: settings.randomSeed ^ game.currentTileId,
    animation: settings.animation,
}))(ChestMiniGameScene);